import React, {useState, useEffect, useCallback} from 'react'
import {useTranslation} from 'react-i18next';
import Api from '../../../../api/SMPFetchHelper';
import {TemplateSchema} from '../../../../api/DBSchema/Template';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import {ObjectTypes} from '../../../../api/consts';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCButton from '../../../../components/Simple/SMCButton';
import SetAdminModal from '../../SetAdminModal';
import {useBoolean} from '../../../../utils/hooks';

function extractAdminId(adminFullName) {
    return adminFullName.substring(
        adminFullName.lastIndexOf('(') + 1,
        adminFullName.lastIndexOf(')')
    );
}

function AdministratorsTab({
    templateObject,
    setTemplateObjectValue
}) {
    const { t } = useTranslation();

    const admins = templateObject[TemplateSchema.admins];
    const [adminsData, setAdminsData] = useState([]);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const [isSetAdminModalOpen, openSetAdminModal, closeSetAdminModal] = useBoolean(false);

    useEffect(() => {
        (async () => {
            if (admins) {
                setIsDataLoading(true);
                const adminsTableData = [];
                for (let adminFullName of admins) {
                    const adminId = extractAdminId(adminFullName);
                    const resp = await Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, adminId);
                    const respData = resp.data.data.administrator;
                    const newAdminData = {
                        fullName: adminFullName,
                        email: respData.general.email,
                        accessRights: respData.general.role
                    };
                    adminsTableData.push(newAdminData);
                }
                setIsDataLoading(false);
                setAdminsData(adminsTableData);
            }
        })();
    }, [admins]);

    const deleteSelected = useCallback(() => {
        if (!admins || !admins.length || !selectedObjects || !selectedObjects.length) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        const adminsWithSelectedRemoved = admins.filter(admin => !selectedObjects.includes(admin));
        setTemplateObjectValue(TemplateSchema.admins, adminsWithSelectedRemoved);
        setSelectedObjects([]);
    }, [admins, selectedObjects, setTemplateObjectValue, t]);

    const handleAdd = useCallback(async (chosenAdmins) => {
        if (chosenAdmins && chosenAdmins.length) {
            const templateAdminFullNames = [];
            for (let adminId of chosenAdmins) {
                const resp = await Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, adminId);
                const respData = resp.data.data.administrator;
                // Build admin full name in the format of template objects
                templateAdminFullNames.push(`${respData.general.firstName} ${respData.general.lastName} (${adminId})`);
            }
            const chosenAdminsWithoutDuplicates = templateAdminFullNames.filter(adminFullName => !admins.includes(adminFullName));
            setTemplateObjectValue(TemplateSchema.admins, [...admins, ...chosenAdminsWithoutDuplicates]);
            closeSetAdminModal();
        }
    }, [admins, setTemplateObjectValue, closeSetAdminModal]);
    
    return (
        <div style={{ width: '100%' }}>
            <SMCLabel>{t('SMC_MESSEGE_SELECT_ADMINS')}</SMCLabel>
            <RowLayout>
                <SMCButton variant="ghost" icon="new" onClick={openSetAdminModal}>{t('SMC_GEN_ADD')}</SMCButton>
                <SMCButton variant="ghost" icon="delete" onClick={deleteSelected}>{t('SMC_GEN_REMOVE')}</SMCButton>
            </RowLayout>
            <SMCTable
                columns={[
                    { id: 'name',         header: t('SMP.NAME'),                               width: 200, accessor: 'fullName'     },
                    { id: 'email',        header: t('SMP.ADMINISTRATORS.TABLE.EMAIL'),         width: 200, accessor: 'email'        },
                    { id: 'accessRights', header: t('SMP.ADMINISTRATORS.TABLE.ACCESS_RIGHTS'), width: 200, accessor: 'accessRights' }
                ]}
                rowIdSelector={obj => obj.fullName}
                content={adminsData}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                height={300}
                loading={isDataLoading}
                hasCheckAll={true}
            />
            <SetAdminModal
                isOpen={isSetAdminModalOpen}
                onRequestClose={closeSetAdminModal}
                onSuccess={handleAdd}
                selectMultiple={true}
                disableSelectById={admins.map(extractAdminId)}
            />
        </div>
    );
}

export default AdministratorsTab;